import { useId, useState } from 'react';
import { ZoomIn } from 'lucide-react';
import { useImageLightbox } from '@/components/common/ImageLightboxContext';

interface BeforeAfterSliderProps {
  beforeSrc: string;
  beforeAlt: string;
  afterSrc: string;
  afterAlt: string;
}

export function BeforeAfterSlider({
  beforeSrc,
  beforeAlt,
  afterSrc,
  afterAlt
}: BeforeAfterSliderProps) {
  const [position, setPosition] = useState(52);
  const comparisonId = useId();
  const { openImage, activeImageId } = useImageLightbox();
  const isImageActive = activeImageId?.startsWith(comparisonId) ?? false;

  const openComparisonImage = (
    src: string,
    alt: string,
    container: HTMLElement | null
  ) => {
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const image = Array.from(container.querySelectorAll('img')).find(
      item => item.src === src
    );
    const aspectRatio =
      image && image.naturalWidth > 0 && image.naturalHeight > 0
        ? image.naturalWidth / image.naturalHeight
        : 16 / 10;

    openImage({
      id: `${comparisonId}-${src === beforeSrc ? 'before' : 'after'}`,
      src,
      alt,
      aspectRatio,
      origin: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      }
    });
  };

  return (
    <div
      className={`group relative aspect-[16/10] w-full overflow-hidden rounded-[var(--radius-md)] bg-surface-muted focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-background ${isImageActive ? '[&_img]:opacity-0' : ''}`}
    >
      <img
        className="absolute inset-0 h-full w-full select-none object-cover"
        src={afterSrc}
        alt={afterAlt}
        loading="lazy"
        draggable={false}
      />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
        aria-hidden="true"
      >
        <img
          className="h-full w-full max-w-none select-none object-cover"
          src={beforeSrc}
          alt=""
          loading="lazy"
          draggable={false}
        />
      </div>

      <div
        className="pointer-events-none absolute inset-y-0 z-10 w-px bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.18)]"
        style={{ left: `${position}%` }}
        aria-hidden="true"
      >
        <span className="absolute left-1/2 top-1/2 grid size-10 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white text-sm font-bold tracking-[-0.15em] text-black shadow-[0_4px_18px_rgba(0,0,0,0.22)]">
          ‹›
        </span>
      </div>

      <input
        className="absolute inset-0 z-20 h-full w-full cursor-ew-resize opacity-0"
        type="range"
        min="8"
        max="92"
        value={position}
        onChange={event => setPosition(Number(event.target.value))}
        aria-label="Pomerite granicu između stanja pre i posle"
        aria-valuetext={`${position}% prikaza pre promene`}
      />

      <button
        type="button"
        className="absolute bottom-3 left-3 z-30 inline-flex cursor-zoom-in items-center gap-1.5 rounded-[var(--radius-sm)] bg-black/68 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary hover:text-on-primary"
        onClick={event =>
          openComparisonImage(
            beforeSrc,
            beforeAlt,
            event.currentTarget.parentElement
          )
        }
        aria-label={`Uvećaj fotografiju pre promene: ${beforeAlt}`}
      >
        <ZoomIn aria-hidden="true" className="size-3.5" />
        Pre
      </button>
      <button
        type="button"
        className="absolute bottom-3 right-3 z-30 inline-flex cursor-zoom-in items-center gap-1.5 rounded-[var(--radius-sm)] bg-black/68 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary hover:text-on-primary"
        onClick={event =>
          openComparisonImage(
            afterSrc,
            afterAlt,
            event.currentTarget.parentElement
          )
        }
        aria-label={`Uvećaj fotografiju posle promene: ${afterAlt}`}
      >
        Posle
        <ZoomIn aria-hidden="true" className="size-3.5" />
      </button>
    </div>
  );
}
